import React, { FC } from 'react'

import { IS_EMBEDDED } from '@sketch/constants'
import { ModalRestrictor } from 'containers/ModalManager'
import { ShareWithoutVersion } from 'modules/versioning'
import { DocumentSidebarLayoutHeaderProps } from 'containers/DocumentSidebarLayout'
import Navbar from 'components/Navbar'
import { useForDesktop, useForTablet } from '@sketch/components'

import HeaderBadge from '../HeaderBadge'
import HeaderNavigation from '../HeaderNavigation'
import HeaderSidebarToggle from '../HeaderSidebarToggle'
import { EditInSketchButton, OpenTemplateButton } from '../EditInSketchButton'

import { VersionStatusButton } from './VersionStatusButton'
import DocumentSettingsDropdown from './DocumentSettingsDropdown'
import { NavbarItem } from './DocumentHeader.styles'
import DocumentNavbar from '../DocumentNavbar'

type DocumentHeaderProps = {
  share: ShareWithoutVersion
  isViewingLatestVersion: boolean
  userCanOpenInApp: boolean
} & Pick<
  DocumentSidebarLayoutHeaderProps,
  'setSidebarLeftOpen' | 'toggleSidebarRight' | 'isSidebarRightOpen'
>

export const DocumentHeader: FC<DocumentHeaderProps> = ({
  share,
  setSidebarLeftOpen,
  toggleSidebarRight,
  isSidebarRightOpen,
  userCanOpenInApp,
  isViewingLatestVersion,
}) => {
  const isDesktop = useForDesktop()
  const isTabletAndBigger = useForTablet()

  const { workspace, type } = share
  const badge = workspace.userIsMember ? 'workspace' : 'home'

  const canOpenInApp = userCanOpenInApp && isDesktop && !IS_EMBEDDED
  const showVersionStatusButton = isTabletAndBigger && !IS_EMBEDDED
  const isTemplate = type === 'TEMPLATE'

  return (
    <Navbar>
      <Navbar.Section>
        <HeaderBadge
          type={badge}
          workspace={workspace}
          setSidebarLeftOpen={setSidebarLeftOpen}
        />

        <HeaderNavigation share={share} />
      </Navbar.Section>

      <Navbar.FixedWidthSection align="end">
        {isTabletAndBigger && (
          <NavbarItem>
            <DocumentNavbar />
          </NavbarItem>
        )}

        {canOpenInApp && isTemplate && (
          <NavbarItem>
            <OpenTemplateButton
              shareId={share.identifier}
              disabled={!isViewingLatestVersion}
            />
          </NavbarItem>
        )}

        {canOpenInApp && !isTemplate && (
          <NavbarItem>
            <EditInSketchButton
              shareId={share.identifier}
              disabled={!isViewingLatestVersion}
            />
          </NavbarItem>
        )}

        {showVersionStatusButton && (
          <NavbarItem>
            <VersionStatusButton
              shareID={share.identifier}
              isViewingLatestVersion={isViewingLatestVersion}
            />
          </NavbarItem>
        )}

        <NavbarItem>
          <ModalRestrictor>
            <DocumentSettingsDropdown />
          </ModalRestrictor>
        </NavbarItem>

        <HeaderSidebarToggle
          toggleSidebar={toggleSidebarRight}
          isSidebarOpen={isSidebarRightOpen}
        />
      </Navbar.FixedWidthSection>
    </Navbar>
  )
}

export default DocumentHeader
